import { useCallback } from 'react';
import { useAuthenticatedFetchWithWorkspace } from '../Keycloak/useAuthenticatedFetchWithWorkspace';
import { QueryGroup, QuerySource } from '../../types/QueryBuilder';
import { convertGroupsToBackendFormat, hasAnyRule } from '../../utils/queryBuilderUtils';
import { useQueryBuilderCache } from '../../context/QueryBuilderCacheContext';
import { useRequestState } from './useRequestState';

// Le nom est enregistré dans QUE_SAVED_QUERY.NAME (VARCHAR2(50)) : au-delà, Oracle refuse l'enregistrement
export const QUERY_NAME_MAX_LENGTH = 50;

// Réponses 409 de l'API : nom déjà porté par une autre requête visible de l'utilisateur (avec uniqueName),
// ou critères modifiés sur une requête ciblée par une campagne (seule une copie peut alors les changer)
export const QUERY_NAME_TAKEN = 'nameTaken' as const;
export const QUERY_USED_IN_CAMPAIGN = 'usedInCampaign' as const;
export type QueryNameTaken = typeof QUERY_NAME_TAKEN;
export type QueryRefusal = QueryNameTaken | typeof QUERY_USED_IN_CAMPAIGN;

export const isQueryRefusal = (result: unknown): result is QueryRefusal =>
    result === QUERY_NAME_TAKEN || result === QUERY_USED_IN_CAMPAIGN;

export const fetchUnlessRefused = async <T,>(request: () => Promise<T>): Promise<T | QueryRefusal> => {
    try {
        return await request();
    } catch (err) {
        if (err instanceof Error && err.message.startsWith('HTTP 409')) {
            return err.message.includes('QUERY_USED_IN_CAMPAIGN') ? QUERY_USED_IN_CAMPAIGN : QUERY_NAME_TAKEN;
        }
        throw err;
    }
};

export interface SaveQueryParams {
    queryId?: string | number;
    description: string;
    groups: QueryGroup[];
    groupId?: number | null;
    source?: QuerySource;
    // Refuser un nom déjà porté par une autre requête (page Ciblage ; le ciblage de campagne ne le demande pas)
    uniqueName?: boolean;
}

interface SaveQueryResponse {
    queryId?: number | string;
    status?: string;
    countInvalidated?: boolean;
}

export interface SavedQueryResult {
    queryId: number;
    // Les critères ont changé : le comptage précédent est effacé et doit être relancé
    countInvalidated: boolean;
}

export const buildQueryPayload = ({ queryId = 0, description, groups, groupId, source, uniqueName }: SaveQueryParams) => ({
    query_id: queryId,
    queryName: description,
    description,
    query_type: 1,
    source: source ?? 'SIMPLE',
    conditions: [convertGroupsToBackendFormat(groups)],
    ...(groupId && groupId > 0 && { group_query_id: groupId }),
    ...(uniqueName && { uniqueName: true }),
});

export const useSaveQuery = () => {
    const { authFetch, isReady } = useAuthenticatedFetchWithWorkspace();
    const cache = useQueryBuilderCache();
    const { loading, error, run } = useRequestState();

    const saveQuery = useCallback(async (params: SaveQueryParams): Promise<SavedQueryResult | QueryRefusal | null> => {
        if (!isReady || !hasAnyRule(params.groups)) return null;

        return run(async () => {
            const response: SaveQueryResponse | QueryRefusal | null = await fetchUnlessRefused(() => authFetch('/saveQuery', {
                method: 'POST',
                body: JSON.stringify(buildQueryPayload(params)),
            }));
            if (isQueryRefusal(response)) return response;
            const isUpdate = params.queryId !== undefined;
            if (!response?.queryId && !(isUpdate && response?.status)) throw new Error('Réponse invalide du serveur');

            cache?.invalidate('saved_queries|');
            return { queryId: Number(response.queryId ?? params.queryId), countInvalidated: !!response.countInvalidated };
        }, 'Erreur lors de la sauvegarde');
    }, [authFetch, isReady, cache, run]);

    return { saveQuery, loading, error };
};
